import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { formatMoney } from "../lib/api";
import { fmtFila } from "../components/indicadores/kpi";
import { useHashHighlight } from "../components/indicadores/useHashHighlight";
import { KpiCompradoSinVenta } from "../components/KpiCompradoSinVenta";
import { SubirArriba } from "../components/SubirArriba";

const ALTA = ["pct_stock_a", "pct_stock_b"];

function RotBox({ label, tone, items, active, testid, span, cols }) {
  const t = { alta: ["border-emerald-500 bg-emerald-50/60", "bg-emerald-600"], baja: ["border-amber-500 bg-amber-50/60", "bg-amber-600"],
              parado: ["border-red-500 bg-red-50/60", "bg-red-600"] }[tone];
  return (
    <div className={`${span} relative rounded-2xl border-2 ${t[0]} p-3 pt-6`} data-testid={`${testid}-box`}>
      <span className={`absolute -top-3 left-4 px-3 py-0.5 rounded-full ${t[1]} text-white text-[11px] font-extrabold tracking-widest`} data-testid={`${testid}-label`}>{label}</span>
      <div className={`grid grid-cols-1 ${cols || (items.length > 1 ? "sm:grid-cols-2" : "")} gap-3`}>
        {items.map((i) => i.key === "comprado_sin_venta"
          ? <div key={i.key} id="ind-comprado_sin_venta" className={"scroll-mt-24 rounded-xl " + (active === i.key ? "ring-2 ring-sky-400" : "")}><KpiCompradoSinVenta /></div>
          : <StockTile key={i.key} i={i} highlight={active === i.key} />)}
      </div>
    </div>
  );
}

function AbcdBoxes({ g, active }) {
  const by = (k) => g.items.filter((i) => k.includes(i.key));
  return (
    <>
      <RotBox label="ALTA ROTACIÓN" tone="alta" items={by(ALTA)} active={active} testid="alta-rotacion" span="sm:col-span-2" />
      <RotBox label="BAJA ROTACIÓN" tone="baja" items={by(["pct_stock_c"])} active={active} testid="baja-rotacion" span="sm:col-span-2 lg:col-span-1" />
      <div className="hidden xl:block" />
      {by(["pct_ventas_a", "pct_ventas_c"]).map((i) => <div key={i.key} className="sm:col-span-2 lg:col-span-1"><StockTile i={i} highlight={active === i.key} /></div>)}
    </>
  );
}

function StockTile({ i, highlight }) {
  const f = { ...i, actual: i.value };
  const body = (
    <>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{i.label}</div>
      {i.top ? (
        <ol className="mt-2 space-y-1.5" data-testid={`ind-stock-${i.key}-lista`}>
          {i.top.map((t, n) => (
            <li key={t.familia} className="flex items-baseline gap-2 text-sm">
              <span className="font-display font-extrabold text-slate-400 w-4">{n + 1}</span>
              <span className="font-semibold truncate flex-1" title={t.familia}>{t.familia}</span>
              <span className={"font-bold whitespace-nowrap " + (i.malo ? "text-red-600" : "text-emerald-700")}>{formatMoney(t.margen_eur)}</span>
              <span className="text-xs text-slate-500 whitespace-nowrap">{String(t.margen_pct ?? "—").replace(".", ",")} %</span>
            </li>
          ))}
          {!i.top.length && <li className="text-sm text-slate-500">Sin ventas en este estudio.</li>}
        </ol>
      ) : <div className={"font-display text-2xl font-extrabold mt-1 " + (i.malo && i.value ? "text-red-600" : "")} data-testid={`ind-stock-${i.key}-valor`}>{fmtFila(f, i.value)}</div>}
      {i.desc && <div className="text-[11px] text-slate-500 mt-1">{i.desc}</div>}
      {i.link && <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold mt-2">Ver detalle →</div>}
      {i.links?.length > 0 && (
        <div className="flex gap-3 mt-2">
          {i.links.map((l) => <Link key={l.to} to={l.to} className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold hover:underline" data-testid={`ind-stock-${i.key}-link-${l.label.toLowerCase()}`}>{l.label} →</Link>)}
        </div>
      )}
    </>
  );
  const cls = "card p-4 block scroll-mt-24 transition-shadow hover:shadow-md " + (highlight ? "ring-2 ring-sky-400" : "") + (i.top ? " sm:col-span-2" : "");
  return i.link
    ? <Link id={`ind-${i.key}`} to={i.link} className={cls} data-testid={`ind-stock-${i.key}`}>{body}</Link>
    : <div id={`ind-${i.key}`} className={cls} data-testid={`ind-stock-${i.key}`}>{body}</div>;
}

export default function IndicadoresStock() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/indicadores/stock").then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "Aún no hay ningún estudio de stock subido." : "No se pudieron cargar los indicadores."));
  }, []);
  const active = useHashHighlight(!!data);

  return (
    <div className="p-4 sm:p-8 max-w-7xl fade-in" data-testid="indicadores-stock-page">
      <SubirArriba />
      <img src="/img/farmanaliza-indicadores-stock.jpg?v=2" alt="Farmanaliza · Indicadores de stock: rotación, cobertura y stock parado"
           className="w-full max-w-5xl rounded-2xl shadow-sm h-auto" data-testid="indicadores-stock-banner" />
      <h1 className="sr-only">Indicadores de stock</h1>
      {data && <p className="text-slate-500 mt-4 text-sm mb-8">Todos los indicadores del estudio <b>{data.study_name}</b>, agrupados por tema.</p>}
      {error && <div className="card p-6 mt-6 max-w-lg text-slate-600" data-testid="indicadores-stock-error">{error} <Link className="text-sky-700 font-semibold" to="/subidas/abcd">Subir estudio</Link></div>}
      {!data && !error && <Loader2 className="animate-spin text-slate-400 mt-6" />}
      {data?.grupos.map((g) => (
        <section key={g.key} className="mb-8" data-testid={`ind-grupo-${g.key}`}>
          <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2">{g.titulo}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {g.key === "abcd" && <AbcdBoxes g={g} active={active} />}
            {g.key === "parado" && <RotBox label="STOCK PARADO" tone="parado" items={g.items} active={active} testid="stock-parado"
                                           span="col-span-full" cols="sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" />}
            {!["abcd", "parado"].includes(g.key) && g.items.map((i) => i.key === "comprado_sin_venta"
              ? <div key={i.key} id="ind-comprado_sin_venta" className={"scroll-mt-24 rounded-xl " + (active === i.key ? "ring-2 ring-sky-400" : "")}><KpiCompradoSinVenta /></div>
              : <StockTile key={i.key} i={i} highlight={active === i.key} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
